import { useState } from "react";
import { useGetAnalytics } from "@workspace/api-client-react";
import { format, subDays } from "date-fns";
import {
  BarChart3, TrendingUp, Clock, Activity,
  Smartphone, Calendar, Info, Globe, Monitor, Tablet, Download,
  Zap, Star, Check,
} from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer,
} from "recharts";
import { type Plan } from "@/components/ui/PlanGate";

interface AnalyticsProps {
  currentPlan?: Plan;
  profileId?: number;
}

function buildTimeSeries(raw: { date: string; count: number }[]) {
  const map = new Map(raw.map((r) => [r.date, r.count]));
  const today = new Date();
  return Array.from({ length: 30 }, (_, i) => {
    const d   = subDays(today, 29 - i);
    const key = format(d, "yyyy-MM-dd");
    return { date: key, label: format(d, "MMM d"), count: map.get(key) ?? 0 };
  });
}

const DEVICE_COLORS: Record<string, { bg: string; text: string; icon: any; label: string }> = {
  mobile:  { bg: "bg-indigo-500",  text: "text-indigo-600",  icon: Smartphone, label: "Mobile"  },
  desktop: { bg: "bg-violet-500",  text: "text-violet-600",  icon: Monitor,    label: "Desktop" },
  tablet:  { bg: "bg-fuchsia-500", text: "text-fuchsia-600", icon: Tablet,     label: "Tablet"  },
};

const isPro     = (plan: Plan) => plan === "pro" || plan === "pro_plus" || plan === "enterprise" || plan === "ent_plus";
const isProPlus = (plan: Plan) => plan === "pro_plus" || plan === "enterprise" || plan === "ent_plus";

const DUMMY_SERIES = Array.from({ length: 30 }, (_, i) => ({
  label: `D${i + 1}`,
  count: Math.floor(5 + Math.sin(i / 3) * 4 + i * 0.3),
}));

// Theme-aware glass: frosted white in light mode, smoked glass in dark mode.
const GLASS_CARD = "backdrop-blur-2xl border shadow-2xl bg-white/60 border-slate-900/10 dark:bg-white/[0.07] dark:border-white/25";

// ── Locked upgrade panels: sample report blurred behind a glass card ───────

async function startCheckout(plan: "pro" | "pro_plus", profileId?: number) {
  try {
    const res = await fetch("/api/v1/subscription/checkout", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("commonqr_token") ?? ""}` },
      body: JSON.stringify({ plan, profile_id: profileId ?? 1 }),
    });
    if (!res.ok) return;
    const { checkoutUrl } = await res.json();
    if (checkoutUrl) window.location.href = checkoutUrl;
  } catch {}
}

const UPGRADES = {
  pro: {
    badge: "10 QR Profiles", title: "Know Your Audience", Icon: Zap,
    desc: "See which devices, locations and time patterns drive your scans.",
    features: ["30-Day Trend Chart", "Device Breakdown", "Location Analytics"],
    cta: "Upgrade to Pro — $5/year",
    grad: "from-violet-600 to-indigo-600", hover: "hover:from-violet-700 hover:to-indigo-700",
    glow: "shadow-violet-500/30", dot: "bg-violet-500/50",
  },
  pro_plus: {
    badge: "30 QR Profiles", title: "Own Your Data", Icon: Star,
    desc: "Export raw scan logs, capture leads directly from your profile.",
    features: ["Everything in Pro", "CSV Export", "Lead Capture Forms"],
    cta: "Upgrade to Pro Plus — $15/year",
    grad: "from-amber-500 to-orange-600", hover: "hover:from-amber-600 hover:to-orange-700",
    glow: "shadow-amber-500/30", dot: "bg-amber-500/50",
  },
} as const;

function GlassOverlay({ plan, profileId }: { plan: "pro" | "pro_plus"; profileId?: number }) {
  const u = UPGRADES[plan];
  const Icon = u.Icon;
  return (
    <div className="absolute inset-0 z-10 flex items-center justify-center rounded-2xl">
      <div className="absolute inset-0 rounded-2xl bg-white/30 dark:bg-black/50" />
      <div className={`relative z-20 w-72 rounded-2xl ${GLASS_CARD} p-4 text-center`}>
        <div className={`absolute -top-3 -right-3 bg-gradient-to-r ${u.grad} text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg ${u.glow} whitespace-nowrap`}>
          {u.badge}
        </div>
        <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${u.grad} flex items-center justify-center mx-auto mb-2 shadow-lg ${u.glow}`}>
          <Icon className="w-4 h-4 text-white" />
        </div>
        <h3 className="text-base font-bold text-foreground dark:text-white mb-1">{u.title}</h3>
        <p className="text-xs text-muted-foreground dark:text-white/70 mb-3 leading-relaxed">{u.desc}</p>
        <div className="flex flex-col gap-1.5 mb-3 text-left">
          {u.features.map((f) => (
            <div key={f} className="flex items-center gap-2 text-xs text-foreground/90 dark:text-white/90">
              <div className={`w-4 h-4 rounded-full ${u.dot} flex items-center justify-center shrink-0`}>
                <Check className="w-2.5 h-2.5 text-white" />
              </div>
              {f}
            </div>
          ))}
        </div>
        <button onClick={() => startCheckout(plan, profileId)} className={`w-full py-2 rounded-xl font-bold text-white bg-gradient-to-r ${u.grad} ${u.hover} shadow-lg ${u.glow} hover:-translate-y-0.5 transition-all text-xs flex items-center justify-center gap-1.5`}>
          {u.cta} <Icon className="w-3.5 h-3.5" />
        </button>
        <p className="text-xs text-muted-foreground dark:text-white/40 mt-1.5">Secure checkout via Stripe. Cancel anytime.</p>
      </div>
    </div>
  );
}

// Sample Pro report (trend chart, devices, countries) — shown blurred behind the glass.
function SampleProReport() {
  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-border bg-card p-4">
        <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-2">Daily Scans — Last 30 Days</h3>
        <ResponsiveContainer width="100%" height={110}>
          <AreaChart data={DUMMY_SERIES} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id="dummyGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#6366f1" stopOpacity={0.4} />
                <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} interval={4} />
            <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} />
            <Area type="monotone" dataKey="count" stroke="#6366f1" strokeWidth={2} fill="url(#dummyGrad)" dot={false} isAnimationActive={false} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-xl border border-border bg-card p-4">
          <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-3">Devices</h3>
          <div className="flex h-3 rounded-full overflow-hidden gap-px mb-3">
            <div className="bg-indigo-500" style={{ width: "60%" }} />
            <div className="bg-violet-500" style={{ width: "30%" }} />
            <div className="bg-fuchsia-500" style={{ width: "10%" }} />
          </div>
          {[["Mobile", "60%", "bg-indigo-500"], ["Desktop", "30%", "bg-violet-500"], ["Tablet", "10%", "bg-fuchsia-500"]].map(([l, p, c]) => (
            <div key={l} className="flex items-center gap-2 text-xs mb-1"><div className={`w-2 h-2 rounded-full ${c}`} /><span className="font-semibold">{l}</span><span className="ml-auto text-muted-foreground">{p}</span></div>
          ))}
        </div>
        <div className="rounded-xl border border-border bg-card p-4">
          <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-3">Top Countries</h3>
          {["AE", "US", "IN", "GB"].map((cc, idx) => (
            <div key={cc} className="mb-1.5">
              <div className="flex justify-between text-xs mb-0.5"><span className="font-semibold">{cc}</span><span className="text-muted-foreground">{20 - idx * 4}</span></div>
              <div className="h-1.5 bg-muted rounded-full overflow-hidden"><div className="h-full bg-indigo-500 rounded-full" style={{ width: `${100 - idx * 22}%` }} /></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Sample Pro Plus report (exports, leads) — shown blurred behind the glass.
function SampleProPlusReport() {
  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-border bg-card p-4">
        <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-2">Leads Captured — Last 30 Days</h3>
        <div className="flex h-[110px] items-end gap-1">
          {DUMMY_SERIES.map((d, i) => (
            <div key={i} className="flex-1 rounded-t bg-gradient-to-t from-amber-500 to-orange-400" style={{ height: `${10 + d.count * 6}%` }} />
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="flex items-center gap-2 mb-2"><Download className="w-4 h-4 text-amber-500" /><span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">CSV Export</span></div>
          {["scan_logs_oct_2026.csv", "scan_logs_sep_2026.csv", "scan_logs_aug_2026.csv"].map((f) => (
            <div key={f} className="text-xs text-muted-foreground flex items-center gap-1.5 mb-1"><div className="w-1.5 h-1.5 rounded-full bg-amber-400" />{f}</div>
          ))}
        </div>
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="flex items-center gap-2 mb-2"><Globe className="w-4 h-4 text-orange-500" /><span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Leads</span></div>
          {["ahmed@example.com", "sarah@example.com", "john@example.com"].map((e) => (
            <div key={e} className="text-xs text-muted-foreground flex items-center gap-1.5 mb-1"><div className="w-1.5 h-1.5 rounded-full bg-orange-400" />{e}</div>
          ))}
        </div>
      </div>
    </div>
  );
}

function LockedPanel({ plan, profileId }: { plan: "pro" | "pro_plus"; profileId?: number }) {
  return (
    <div className="relative min-h-[330px] overflow-hidden rounded-2xl border border-border">
      <div className="pointer-events-none select-none p-3 filter blur-[5px] opacity-75" aria-hidden="true">
        {plan === "pro" ? <SampleProReport /> : <SampleProPlusReport />}
      </div>
      <GlassOverlay plan={plan} profileId={profileId} />
    </div>
  );
}

// ── Recent scans (latest 5, expandable) ────────────────────────────────────

type ScanRow = { id: number | string; scannedAt: string; deviceType?: string | null; countryCode?: string | null; city?: string | null };

const DEVICE_PILL: Record<string, string> = {
  mobile:  "bg-indigo-500/10 text-indigo-600 dark:text-indigo-300",
  tablet:  "bg-fuchsia-500/10 text-fuchsia-600 dark:text-fuchsia-300",
  desktop: "bg-violet-500/10 text-violet-600 dark:text-violet-300",
};

function RecentScans({ scans }: { scans: ScanRow[] }) {
  const [showAll, setShowAll] = useState(false);
  const rows = showAll ? scans : scans.slice(0, 5);
  return (
    <div className="h-full rounded-2xl border border-border bg-card shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-widest">Recent Scans</h3>
        {scans.length > 5 && (
          <button onClick={() => setShowAll((v) => !v)} className="text-xs font-semibold text-primary hover:underline">
            {showAll ? "Show less" : `Show all (${scans.length})`}
          </button>
        )}
      </div>
      {scans.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
          <BarChart3 className="w-8 h-8 opacity-20 mb-2" />
          <p className="font-medium text-sm">No scans yet</p>
          <p className="text-xs mt-1">Share your QR code to start tracking engagement.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/40 text-left">
                <th className="px-4 py-2 text-xs font-semibold text-muted-foreground">Time</th>
                <th className="px-4 py-2 text-xs font-semibold text-muted-foreground">Device</th>
                <th className="px-4 py-2 text-xs font-semibold text-muted-foreground">Country</th>
                <th className="px-4 py-2 text-xs font-semibold text-muted-foreground">City</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((scan) => {
                const type = scan.deviceType ?? "desktop";
                const DevIcon = DEVICE_COLORS[type]?.icon ?? Monitor;
                return (
                  <tr key={scan.id} className="border-t border-border/50">
                    <td className="px-4 py-2 font-mono text-xs text-muted-foreground whitespace-nowrap">{format(new Date(scan.scannedAt), "MMM d, h:mm a")}</td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${DEVICE_PILL[type] ?? DEVICE_PILL.desktop}`}>
                        <DevIcon className="h-3 w-3" /> {type}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-sm">{scan.countryCode ?? <span className="text-muted-foreground/40">—</span>}</td>
                    <td className="px-4 py-2 text-sm text-muted-foreground">{scan.city ?? <span className="text-muted-foreground/40">—</span>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Main ───────────────────────────────────────────────────────────────────

export default function Analytics({ currentPlan = "free", profileId }: AnalyticsProps) {
  const { data, isLoading, isError, error } = useGetAnalytics();

  if (isLoading) {
    return (
      <div className="py-20 text-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-3" />
        <p className="text-muted-foreground text-sm">Loading analytics...</p>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="py-20 text-center">
        <p className="text-muted-foreground text-sm font-medium">Failed to load analytics.</p>
        <p className="text-xs text-muted-foreground/60 mt-1">{String(error ?? "Unknown error")}</p>
      </div>
    );
  }

  const timeSeries      = buildTimeSeries((data as any).timeSeries ?? []);
  const deviceBreakdown = (data as any).deviceBreakdown ?? { mobile: 0, desktop: 0, tablet: 0 };
  const topCountries: { countryCode: string; count: number }[] = (data as any).topCountries ?? [];
  const todayScans: number   = (data as any).todayScans   ?? 0;
  const weekScans: number    = (data as any).weekScans    ?? 0;
  const monthScans: number   = (data as any).monthScans   ?? 0;
  const dailyAverage: number = (data as any).dailyAverage ?? 0;

  const deviceTotal = (deviceBreakdown.mobile ?? 0) + (deviceBreakdown.desktop ?? 0) + (deviceBreakdown.tablet ?? 0);
  const devicePct   = (key: string) =>
    deviceTotal > 0 ? Math.round(((deviceBreakdown[key] ?? 0) / deviceTotal) * 100) : 0;

  const kpis = [
    { label: "Total Scans",   value: data.totalScans.toLocaleString(), sub: "All time", icon: BarChart3, hero: true },
    { label: "Today",         value: todayScans,   sub: "Scans today",       icon: Activity,   color: "text-emerald-500", bg: "bg-emerald-500/10" },
    { label: "This Week",     value: weekScans,    sub: "Last 7 days",       icon: Calendar,   color: "text-blue-500",    bg: "bg-blue-500/10"    },
    { label: "This Month",    value: monthScans,   sub: "Last 30 days",      icon: TrendingUp, color: "text-orange-500",  bg: "bg-orange-500/10"  },
    { label: "Daily Average", value: dailyAverage, sub: "Scans / day (30d)", icon: Clock,      color: "text-pink-500",    bg: "bg-pink-500/10"    },
    {
      label: "Last Scanned",
      value: data.lastScannedAt ? format(new Date(data.lastScannedAt), "MMM d") : "Never",
      sub:   data.lastScannedAt ? format(new Date(data.lastScannedAt), "h:mm a") : "No scans yet",
      icon: Smartphone, color: "text-slate-500 dark:text-slate-300", bg: "bg-slate-500/10",
    },
  ];

  const hasScans      = data.totalScans > 0;
  const userIsPro     = isPro(currentPlan);
  const userIsProPlus = isProPlus(currentPlan);
  const scans         = data.recentScans as unknown as ScanRow[];

  return (
    <div className="space-y-5">

      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-2xl font-display font-bold">Analytics</h2>
          <p className="text-muted-foreground text-sm mt-1">Real-time insights on your QR profile engagement.</p>
        </div>
        <div className="flex items-center gap-2">
          {userIsProPlus && (
            <button onClick={() => window.open("/api/v1/analytics/export", "_blank")} className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border border-border text-muted-foreground hover:text-primary hover:border-primary transition-colors">
              <Download className="w-3.5 h-3.5" /> Export CSV
            </button>
          )}
          <div className="hidden sm:flex items-center gap-1.5 text-xs text-muted-foreground bg-muted/50 px-3 py-1.5 rounded-full border border-border">
            <Info className="w-3.5 h-3.5" /> Updates on every scan
          </div>
        </div>
      </div>

      {/* 6 KPI tiles — one slim row on desktop */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {kpis.map((kpi) => {
          const Icon = kpi.icon;
          if ((kpi as any).hero) {
            return (
              <div key={kpi.label} className="rounded-xl p-4 bg-gradient-to-br from-violet-600 to-indigo-600 text-white shadow-lg shadow-indigo-500/20">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-white/80">{kpi.label}</span>
                  <Icon className="w-4 h-4 text-white/80" />
                </div>
                <p className="mt-2 text-2xl font-bold leading-none">{kpi.value}</p>
                <p className="mt-1 text-[11px] text-white/70">{kpi.sub}</p>
              </div>
            );
          }
          return (
            <div key={kpi.label} className="rounded-xl p-4 bg-card border border-border shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground">{kpi.label}</span>
                <span className={`flex h-6 w-6 items-center justify-center rounded-md ${(kpi as any).bg}`}><Icon className={`w-3.5 h-3.5 ${(kpi as any).color}`} /></span>
              </div>
              <p className={`mt-2 text-2xl font-bold leading-none ${(kpi as any).color}`}>{kpi.value}</p>
              <p className="mt-1 text-[11px] text-muted-foreground">{kpi.sub}</p>
            </div>
          );
        })}
      </div>

      {userIsPro ? (
        <>
          {/* Trend + devices */}
          <div className="grid gap-4 lg:grid-cols-3">
            <div className="lg:col-span-2 rounded-2xl border border-border bg-card p-4 shadow-sm">
              <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-3">Daily Scans — Last 30 Days</h3>
              {!hasScans ? (
                <div className="h-32 flex items-center justify-center text-muted-foreground text-sm">No scan data yet — share your QR to start tracking.</div>
              ) : (
                <ResponsiveContainer width="100%" height={150}>
                  <AreaChart data={timeSeries} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="scanGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor="#6366f1" stopOpacity={0.25} />
                        <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} interval={4} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} />
                    <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "0.75rem", fontSize: 12 }} labelStyle={{ fontWeight: 600 }} formatter={(v: number) => [v, "Scans"]} />
                    <Area type="monotone" dataKey="count" stroke="#6366f1" strokeWidth={2} fill="url(#scanGrad)" dot={false} activeDot={{ r: 4, fill: "#6366f1" }} />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>

            <div className="rounded-2xl border border-border bg-card p-4 shadow-sm">
              <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-3">Device Breakdown</h3>
              {!hasScans ? <p className="text-sm text-muted-foreground">No data yet.</p> : (
                <>
                  <div className="flex h-3 rounded-full overflow-hidden gap-px mb-4">
                    {(["mobile", "desktop", "tablet"] as const).map((key) => {
                      const pct = devicePct(key);
                      return pct > 0 ? <div key={key} className={`${DEVICE_COLORS[key].bg} transition-all`} style={{ width: `${pct}%` }} title={`${DEVICE_COLORS[key].label}: ${pct}%`} /> : null;
                    })}
                  </div>
                  <div className="space-y-2">
                    {(["mobile", "desktop", "tablet"] as const).map((key) => {
                      const cfg = DEVICE_COLORS[key]; const DIcon = cfg.icon;
                      return (
                        <div key={key} className="flex items-center gap-2">
                          <div className={`w-2.5 h-2.5 rounded-full ${cfg.bg}`} />
                          <DIcon className={`w-3.5 h-3.5 ${cfg.text}`} />
                          <span className="text-sm font-semibold">{cfg.label}</span>
                          <span className="ml-auto text-sm text-muted-foreground">{devicePct(key)}%</span>
                          <span className="text-xs text-muted-foreground/60">({(deviceBreakdown[key] ?? 0).toLocaleString()})</span>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Countries + recent scans */}
          <div className="grid gap-4 lg:grid-cols-3">
            <div className="rounded-2xl border border-border bg-card p-4 shadow-sm">
              <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-3">Top Countries</h3>
              {topCountries.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-6 text-muted-foreground/50">
                  <Globe className="w-7 h-7 mb-2" />
                  <p className="text-sm">No country data yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {topCountries.map((c) => {
                    const pct = Math.round((c.count / topCountries[0].count) * 100);
                    return (
                      <div key={c.countryCode}>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="font-semibold">{c.countryCode}</span>
                          <span className="text-muted-foreground">{c.count.toLocaleString()}</span>
                        </div>
                        <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                          <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="lg:col-span-2">
              <RecentScans scans={scans} />
            </div>
          </div>
        </>
      ) : (
        <RecentScans scans={scans} />
      )}

      {/* Locked Pro / Pro Plus — sample reports behind glass, side by side */}
      {!userIsProPlus && (
        <div className={`grid gap-4 ${userIsPro ? "" : "lg:grid-cols-2"}`}>
          {!userIsPro && <LockedPanel plan="pro" profileId={profileId} />}
          <LockedPanel plan="pro_plus" profileId={profileId} />
        </div>
      )}

    </div>
  );
}
